/**
 * MedMicAuthCoordinator — sole MedMic auth owner (Phase 4).
 *
 * Public API:
 *   initialize() | connect() | disconnect() | refresh() | handleAuthFailure()
 *   getSnapshot() | subscribe() | publishCurrentSession() | startBridge() | dispose()
 *
 * Boundaries:
 * - MSAL = identity/token provider only (silent acquire helpers)
 * - Coordinator = only acquire / Validate / refresh / commit / publish owner
 * - CRE_DESKTOP_AGENT_SESSION = only page→extension auth protocol
 * - Enterprise Zustand is never written by MedMic paths
 * - Disconnect latch blocks silent reconnect until explicit connect()
 */

import { CRE_EXTENSION_PATH } from "../constants/routes";
import {
  getCreAccounts,
  getCreActiveAccount,
  isCreAccount,
} from "./creAccountUtils";
import { getCreAccessToken } from "./creAuthConfig";
import {
  CRE_DESKTOP_AGENT_AUTH_REQUEST,
  CRE_DESKTOP_AGENT_SESSION,
  CRE_EXTENSION_AUTH_FAILURE,
  CRE_EXTENSION_DISCONNECT_REQUEST,
  MEDMIC_AUTH_BROADCAST_CHANNEL,
} from "./creExtensionConstants";
import { getCreMsalInstance, initializeCreMsal } from "./creMsalInstance";
import {
  clearMedMicDisconnectLatchForTests,
  readMedMicDisconnectLatch,
  writeMedMicDisconnectLatch,
} from "./medmicDisconnectLatch";
import { getSafeTokenMeta, logMedmicAuthDiag } from "./creTokenDiagnostics";
import {
  compareMedMicSessionOrder,
  createMedMicSessionId,
  getMedMicAuthSnapshot,
  patchMedMicAuthStore,
  replaceMedMicAuthStore,
  resetMedMicAuthStoreForTests,
  useMedMicAuthStore,
} from "./medmicAuthStore";
import { validateMedmicToken } from "./validateMedmicToken";
import { consumeMedMicPreferActiveAccount } from "./medmicExplicitConnect";
import {
  acquireAndValidateRefreshCandidate,
  classifyRefreshFailure,
  clearRefreshLeaseForTests,
  computeProactiveRefreshDelayMs,
  invalidateAllRefreshLeases,
  isTokenExpired,
  REFRESH_TIMEOUT_MS,
  releaseRefreshLease,
  TEMP_REFRESH_RETRY_DELAY_MS,
  tryClaimRefreshLease,
} from "./medmicTokenRefresh";

const HANDOFF_KEY = "cre_extension_handoff";
const DEFAULT_TOKEN_TTL_MS = 55 * 60 * 1000;
const ACQUIRE_TIMEOUT_MS = 45_000;

let bridgeStarted = false;
/** @type {Promise<boolean> | null} */
let connectInFlight = null;
/** @type {Promise<boolean> | null} */
let refreshInFlight = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let proactiveTimerId = null;
/** @type {AbortController | null} */
let activeAbortController = null;
/** @type {BroadcastChannel | null} */
let broadcastChannel = null;
const tabId =
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tab-${Date.now()}`;

let hydratedFromLatch = false;
/** @type {boolean} */
let coordinatorReady = false;
/** @type {Promise<void> | null} */
let initializePromise = null;

const ALLOWED_TRANSITIONS = {
  signed_out: [
    "authenticating",
    "disconnected",
    "interaction_required",
    "validation_failed",
  ],
  authenticating: [
    "validating",
    "validation_failed",
    "signed_out",
    "disconnecting",
    "disconnected",
    "interaction_required",
  ],
  validating: [
    "authenticated",
    "validation_failed",
    "disconnecting",
    "disconnected",
    "signed_out",
  ],
  authenticated: [
    "refreshing",
    "disconnecting",
    "disconnected",
    "expired",
    "interaction_required",
    "refresh_failed",
  ],
  refreshing: [
    "authenticated",
    "refresh_failed",
    "interaction_required",
    "expired",
    "disconnecting",
    "disconnected",
    "validation_failed",
  ],
  refresh_failed: [
    "refreshing",
    "authenticated",
    "interaction_required",
    "expired",
    "disconnecting",
    "disconnected",
    "authenticating",
  ],
  expired: ["authenticating", "disconnected", "interaction_required"],
  interaction_required: [
    "authenticating",
    "disconnected",
    "signed_out",
  ],
  validation_failed: ["authenticating", "disconnected", "signed_out"],
  disconnecting: ["disconnected"],
  disconnected: ["authenticating", "signed_out"],
};

/**
 * @param {string} from
 * @param {string} to
 */
function assertTransition(from, to) {
  if (from === to) return true;
  const allowed = ALLOWED_TRANSITIONS[from];
  if (allowed && allowed.includes(to)) return true;
  logMedmicAuthDiag("medmic", "invalid_state_transition", { from, to });
  return false;
}

/**
 * @param {Partial<import("./medmicAuthStore").MedMicAuthSnapshot>} patch
 */
function safePatchState(patch) {
  if (patch.state) {
    const current = getMedMicAuthSnapshot().state;
    if (!assertTransition(current, patch.state)) {
      // Never coerce into authenticated via an invalid transition.
      if (patch.state === "authenticated") {
        logMedmicAuthDiag("medmic", "blocked_invalid_authenticated_commit", {
          from: current,
        });
        return false;
      }
    }
  }
  patchMedMicAuthStore(patch);
  return true;
}

function newId(prefix) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}`;
}

export function isMedmicExtensionSurface() {
  if (typeof window === "undefined") return false;
  return window.location.pathname.startsWith(CRE_EXTENSION_PATH);
}

function storeExtensionHandoff(payload) {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

function clearExtensionHandoffStorage() {
  try {
    sessionStorage.removeItem(HANDOFF_KEY);
  } catch {
    /* ignore */
  }
}

function persistLatchFromSnapshot(snap) {
  writeMedMicDisconnectLatch({
    disconnected: Boolean(snap.disconnected) || snap.state === "disconnected",
    sessionId: snap.sessionId,
    generation: snap.generation,
    epoch: snap.epoch,
    lastUserEmail: snap.userEmail ?? null,
    lastAccountIdentifier: snap.homeAccountId ?? null,
    lastLocalAccountId: snap.localAccountId ?? null,
  });
}

/**
 * Hydrate in-memory store from persisted latch once per page load.
 */
export function hydrateMedMicAuthFromLatch() {
  if (hydratedFromLatch) return getMedMicAuthSnapshot();
  hydratedFromLatch = true;
  const latch = readMedMicDisconnectLatch();
  if (!latch) return getMedMicAuthSnapshot();

  if (latch.disconnected) {
    replaceMedMicAuthStore({
      state: "disconnected",
      epoch: latch.epoch || 0,
      sessionId: latch.sessionId || createMedMicSessionId(),
      generation: latch.generation || 0,
      disconnected: true,
      accessToken: undefined,
      userEmail: latch.lastUserEmail ?? null,
      expiresAt: null,
      operationId: undefined,
      error: null,
      user: null,
      homeAccountId: latch.lastAccountIdentifier ?? null,
      localAccountId: latch.lastLocalAccountId ?? null,
      reason: "persisted_latch",
    });
  } else {
    // Identity hints only — not authenticated until Validate.
    patchMedMicAuthStore({
      epoch: Math.max(getMedMicAuthSnapshot().epoch, latch.epoch || 0),
      homeAccountId: latch.lastAccountIdentifier ?? null,
      localAccountId: latch.lastLocalAccountId ?? null,
      userEmail: latch.lastUserEmail ?? null,
      disconnected: false,
    });
  }
  return getMedMicAuthSnapshot();
}

/**
 * @param {{
 *   sessionId: string,
 *   operationId: string,
 *   baseGeneration: number,
 *   baseEpoch: number,
 * }} op
 */
function isOperationCurrent(op) {
  const snap = getMedMicAuthSnapshot();
  return (
    snap.sessionId === op.sessionId &&
    snap.operationId === op.operationId &&
    snap.generation === op.baseGeneration &&
    snap.epoch === op.baseEpoch &&
    !snap.disconnected
  );
}

/**
 * @param {string} source
 * @param {{ rotateSession?: boolean }} [opts]
 */
function beginOperation(source, opts = {}) {
  const snap = getMedMicAuthSnapshot();
  let epoch = snap.epoch;
  let sessionId = snap.sessionId;
  let generation = snap.generation;

  if (opts.rotateSession || snap.disconnected || snap.state === "disconnected") {
    epoch = snap.epoch + 1;
    sessionId = createMedMicSessionId();
    generation = 0;
  }

  const operationId = newId("op");
  const op = {
    sessionId,
    operationId,
    baseGeneration: generation,
    baseEpoch: epoch,
    source,
  };

  if (activeAbortController) {
    try {
      activeAbortController.abort();
    } catch {
      /* ignore */
    }
  }
  activeAbortController = new AbortController();

  patchMedMicAuthStore({
    state: "authenticating",
    epoch,
    sessionId,
    generation,
    disconnected: false,
    operationId,
    error: null,
    reason: null,
    accessToken: undefined,
    expiresAt: null,
    user: null,
  });

  persistLatchFromSnapshot(getMedMicAuthSnapshot());

  logMedmicAuthDiag("medmic", "operation_begin", {
    source,
    operationId,
    sessionId,
    baseGeneration: generation,
    epoch,
  });
  return op;
}

function commitFailure(op, error, state = "validation_failed") {
  if (!isOperationCurrent(op)) {
    logMedmicAuthDiag("medmic", "commit_failure_discarded_stale", {
      source: op.source,
      operationId: op.operationId,
      code: error.code,
    });
    return false;
  }
  patchMedMicAuthStore({
    state,
    error,
    accessToken: undefined,
    expiresAt: null,
    user: null,
    operationId: undefined,
  });
  persistLatchFromSnapshot(getMedMicAuthSnapshot());
  MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
  return true;
}

function commitAuthenticated(op, data) {
  if (!isOperationCurrent(op)) {
    logMedmicAuthDiag("medmic", "commit_auth_discarded_stale", {
      source: op.source,
      operationId: op.operationId,
    });
    return false;
  }

  const nextGeneration = op.baseGeneration + 1;
  if (
    !safePatchState({
      state: "authenticated",
      generation: nextGeneration,
      accessToken: data.accessToken,
      userEmail: data.userEmail,
      expiresAt: data.expiresAt,
      user: data.user,
      homeAccountId: data.homeAccountId ?? null,
      localAccountId: data.localAccountId ?? null,
      disconnected: false,
      error: null,
      errorCode: null,
      operationId: undefined,
      reason: null,
      refreshSource: data.refreshSource ?? null,
      refreshWarning: false,
    })
  ) {
    return false;
  }

  persistLatchFromSnapshot(getMedMicAuthSnapshot());

  logMedmicAuthDiag("medmic", "commit_authenticated", {
    source: op.source,
    operationId: op.operationId,
    sessionId: op.sessionId,
    generation: nextGeneration,
    epoch: op.baseEpoch,
    fingerprint: getSafeTokenMeta(data.accessToken).fingerprint,
    refreshSource: data.refreshSource ?? null,
  });
  scheduleProactiveRefreshTimer();
  return true;
}

function clearProactiveRefreshTimer() {
  if (proactiveTimerId != null) {
    clearTimeout(proactiveTimerId);
    proactiveTimerId = null;
  }
}

function scheduleProactiveRefreshTimer() {
  clearProactiveRefreshTimer();
  const snap = getMedMicAuthSnapshot();
  if (snap.disconnected || snap.state === "disconnected") return;
  if (snap.state !== "authenticated" && snap.state !== "refreshing") return;
  if (!snap.accessToken || !snap.expiresAt) return;

  const delay = computeProactiveRefreshDelayMs(snap.expiresAt);
  logMedmicAuthDiag("medmic", "proactive_refresh_scheduled", {
    delayMs: delay,
    expiresAt: snap.expiresAt,
    epoch: snap.epoch,
    generation: snap.generation,
    tabId,
  });

  proactiveTimerId = setTimeout(() => {
    proactiveTimerId = null;
    void MedMicAuthCoordinator.refresh({ source: "proactive" });
  }, delay);
}

/**
 * @param {{
 *   sessionId: string,
 *   operationId: string,
 *   baseGeneration: number,
 *   baseEpoch: number,
 * }} op
 */
function isRefreshOperationCurrent(op) {
  const snap = getMedMicAuthSnapshot();
  return (
    snap.sessionId === op.sessionId &&
    snap.operationId === op.operationId &&
    snap.generation === op.baseGeneration &&
    snap.epoch === op.baseEpoch &&
    !snap.disconnected
  );
}

/**
 * @param {{ source: "proactive" | "http_401" | "auth_request" }} opts
 * @returns {Promise<boolean>}
 */
async function runRefreshPipeline(opts) {
  const source = opts.source;
  hydrateMedMicAuthFromLatch();
  const snap = getMedMicAuthSnapshot();

  if (snap.disconnected || snap.state === "disconnected") {
    logMedmicAuthDiag("medmic", "refresh_blocked_disconnected", { source });
    return false;
  }

  if (
    snap.state !== "authenticated" &&
    snap.state !== "refreshing" &&
    snap.state !== "refresh_failed" &&
    snap.state !== "expired"
  ) {
    // Allow refresh from authenticated path primarily; refresh_failed with
    // still-valid token may retry once via timer.
    if (!(snap.refreshWarning && snap.accessToken && !isTokenExpired(snap.expiresAt))) {
      logMedmicAuthDiag("medmic", "refresh_blocked_state", {
        source,
        state: snap.state,
      });
      return false;
    }
  }

  if (refreshInFlight) {
    logMedmicAuthDiag("medmic", "refresh_join_inflight", { source });
    return refreshInFlight;
  }

  const operationId = newId("refresh");
  const op = {
    sessionId: snap.sessionId,
    operationId,
    baseGeneration: snap.generation,
    baseEpoch: snap.epoch,
    source,
  };

  if (!tryClaimRefreshLease(tabId, op)) {
    logMedmicAuthDiag("medmic", "refresh_lease_not_owned", {
      source,
      tabId,
      operationId,
    });
    // Another tab owns refresh — wait for broadcast SESSION; do not mint.
    return false;
  }

  clearProactiveRefreshTimer();

  if (activeAbortController) {
    try {
      activeAbortController.abort();
    } catch {
      /* ignore */
    }
  }
  activeAbortController = new AbortController();
  const signal = activeAbortController.signal;

  if (
    !safePatchState({
      state: "refreshing",
      operationId,
      refreshSource: source,
      error: null,
      errorCode: null,
      // Keep existing accessToken until Validate succeeds.
    })
  ) {
    releaseRefreshLease(tabId);
    return false;
  }
  MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });

  refreshInFlight = (async () => {
    const startedAt = Date.now();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      try {
        activeAbortController?.abort();
      } catch {
        /* ignore */
      }
    }, REFRESH_TIMEOUT_MS);

    try {
      const result = await Promise.race([
        acquireAndValidateRefreshCandidate({
          source,
          homeAccountId: snap.homeAccountId,
          signal,
        }),
        new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error("refresh_timeout")),
            REFRESH_TIMEOUT_MS,
          );
        }),
      ]);

      if (timedOut || !isRefreshOperationCurrent(op)) {
        logMedmicAuthDiag("medmic", "refresh_discarded_stale", {
          source,
          operationId,
          timedOut,
        });
        return false;
      }

      if (!result.ok) {
        const classified = classifyRefreshFailure(
          result.validate || { ok: false, error: { code: "acquire_failed" } },
          result.acquire,
        );
        return applyRefreshFailure(op, classified, source);
      }

      if (!isRefreshOperationCurrent(op)) {
        logMedmicAuthDiag("medmic", "refresh_commit_discarded_stale", {
          source,
          operationId,
        });
        return false;
      }

      const expiresAt =
        result.meta?.expiresAtMs ?? Date.now() + DEFAULT_TOKEN_TTL_MS;
      const userEmail =
        result.userEmail ||
        snap.userEmail ||
        (typeof result.account?.username === "string"
          ? result.account.username
          : null);

      const committed = commitAuthenticated(op, {
        accessToken: result.accessToken,
        userEmail,
        user: result.user,
        expiresAt,
        homeAccountId: result.account?.homeAccountId ?? snap.homeAccountId,
        localAccountId: result.account?.localAccountId ?? snap.localAccountId,
        refreshSource: source,
      });
      if (!committed) return false;

      logMedmicAuthDiag("medmic", "refresh_success", {
        source,
        operationId,
        epoch: op.baseEpoch,
        baseGeneration: op.baseGeneration,
        generation: getMedMicAuthSnapshot().generation,
        elapsedMs: Date.now() - startedAt,
        tabId,
      });

      MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
      return true;
    } catch (err) {
      if (!isRefreshOperationCurrent(op)) return false;
      const message = err instanceof Error ? err.message : String(err);
      const classified =
        message === "refresh_timeout"
          ? {
              terminal: false,
              state: "refresh_failed",
              code: "refresh_timeout",
              message,
            }
          : classifyRefreshFailure(
              { ok: false, error: { code: "refresh_error", message } },
              { ok: false, code: "refresh_error", message },
            );
      return applyRefreshFailure(op, classified, source);
    } finally {
      clearTimeout(timeoutId);
      if (getMedMicAuthSnapshot().operationId === operationId) {
        patchMedMicAuthStore({ operationId: undefined });
      }
      refreshInFlight = null;
      releaseRefreshLease(tabId);
    }
  })();

  return refreshInFlight;
}

/**
 * @param {ReturnType<typeof Object>} op
 * @param {{ terminal: boolean, state: string, code: string, message: string }} classified
 * @param {string} source
 */
function applyRefreshFailure(op, classified, source) {
  if (!isRefreshOperationCurrent(op)) return false;

  const snap = getMedMicAuthSnapshot();
  const tokenStillValid =
    Boolean(snap.accessToken) && !isTokenExpired(snap.expiresAt);

  logMedmicAuthDiag("medmic", "refresh_failure_classified", {
    source,
    operationId: op.operationId,
    code: classified.code,
    terminal: classified.terminal,
    tokenStillValid,
    tabId,
  });

  // API 401 recovery path: never keep a generation the backend rejected.
  if (source === "http_401") {
    patchMedMicAuthStore({
      state: classified.terminal ? classified.state : "interaction_required",
      accessToken: undefined,
      expiresAt: null,
      error: { code: classified.code, message: classified.message },
      errorCode: classified.code,
      refreshWarning: false,
      operationId: undefined,
      reason: source,
      refreshSource: source,
    });
    persistLatchFromSnapshot(getMedMicAuthSnapshot());
    clearProactiveRefreshTimer();
    MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
    return false;
  }

  // Proactive temporary failure with still-valid token: keep authenticated.
  if (!classified.terminal && tokenStillValid) {
    patchMedMicAuthStore({
      state: "authenticated",
      error: { code: classified.code, message: classified.message },
      errorCode: classified.code,
      refreshWarning: true,
      operationId: undefined,
      refreshSource: source,
    });
    MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
    // One controlled retry.
    clearProactiveRefreshTimer();
    proactiveTimerId = setTimeout(() => {
      proactiveTimerId = null;
      void MedMicAuthCoordinator.refresh({ source: "proactive" });
    }, TEMP_REFRESH_RETRY_DELAY_MS);
    return false;
  }

  const nextState = isTokenExpired(snap.expiresAt)
    ? "expired"
    : classified.state;

  patchMedMicAuthStore({
    state: nextState,
    accessToken: undefined,
    expiresAt: null,
    error: { code: classified.code, message: classified.message },
    errorCode: classified.code,
    refreshWarning: false,
    operationId: undefined,
    reason: source,
    refreshSource: source,
  });
  persistLatchFromSnapshot(getMedMicAuthSnapshot());
  clearProactiveRefreshTimer();
  MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
  return false;
}

function sessionAllowsToken(snap) {
  if (!snap.accessToken) return false;
  if (snap.disconnected || snap.state === "disconnected") return false;
  if (snap.state === "signed_out") return false;
  if (snap.state === "expired") return false;
  if (snap.state === "interaction_required") return false;
  if (snap.state === "validation_failed") return false;
  if (isTokenExpired(snap.expiresAt)) return false;

  if (snap.state === "authenticated" || snap.state === "refreshing") {
    return true;
  }

  // Proactive temporary failure may keep a still-valid non-rejected token.
  if (
    snap.state === "refresh_failed" &&
    snap.refreshWarning &&
    snap.refreshSource === "proactive"
  ) {
    return true;
  }

  return false;
}

/**
 * Whether an extension AUTH_REQUEST should run silent refresh before SESSION.
 * Snapshot-only when the committed MedMic token is still publishable.
 *
 * @param {import("./medmicAuthStore").MedMicAuthSnapshot} snap
 * @param {number} [now]
 */
export function shouldRefreshMedMicSession(snap, now = Date.now()) {
  if (snap.disconnected || snap.state === "disconnected") return false;
  if (
    snap.state === "interaction_required" ||
    snap.state === "signed_out" ||
    snap.state === "validation_failed"
  ) {
    return false;
  }
  if (!snap.homeAccountId) return false;

  const refreshableState =
    snap.state === "authenticated" ||
    snap.state === "refreshing" ||
    snap.state === "refresh_failed" ||
    snap.state === "expired";

  if (!refreshableState) return false;
  if (!snap.accessToken) return true;
  if (snap.state === "expired") return true;
  return isTokenExpired(snap.expiresAt, now);
}

/**
 * Resolve which MSAL account may be used.
 * @param {"connect" | "bootstrap"} mode
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 */
function resolveMedmicAccount(mode, instance) {
  const snap = getMedMicAuthSnapshot();
  const latch = readMedMicDisconnectLatch();
  const knownHomeId =
    snap.homeAccountId || latch?.lastAccountIdentifier || null;
  const accounts = getCreAccounts(instance);
  const preferActive =
    mode === "connect" && consumeMedMicPreferActiveAccount();

  if (mode === "bootstrap") {
    if (!knownHomeId) {
      logMedmicAuthDiag("medmic", "bootstrap_no_known_account", {
        creAccountCount: accounts.length,
      });
      return { account: null, reason: "no_known_medmic_account" };
    }
    const match = accounts.find((a) => a.homeAccountId === knownHomeId);
    if (!match) {
      logMedmicAuthDiag("medmic", "bootstrap_account_mismatch", {
        knownHomeIdPresent: true,
        creAccountCount: accounts.length,
      });
      return { account: null, reason: "account_mismatch" };
    }
    return { account: match, reason: null };
  }

  // After "Use another account", prefer the MSAL active account from the new login.
  if (preferActive) {
    const active = getCreActiveAccount(instance);
    if (active && isCreAccount(active)) {
      logMedmicAuthDiag("medmic", "connect_prefer_active_account", {
        homeIdPresent: Boolean(active.homeAccountId),
        creAccountCount: accounts.length,
      });
      return { account: active, reason: null };
    }
  }

  // Explicit connect: prefer known MedMic account, else active CRE, else single CRE.
  if (knownHomeId) {
    const match = accounts.find((a) => a.homeAccountId === knownHomeId);
    if (match) return { account: match, reason: null };
  }
  const active = getCreActiveAccount(instance);
  if (active && isCreAccount(active)) {
    if (accounts.length > 1 && knownHomeId && active.homeAccountId !== knownHomeId) {
      return { account: null, reason: "ambiguous_accounts" };
    }
    return { account: active, reason: null };
  }
  if (accounts.length === 1) return { account: accounts[0], reason: null };
  if (accounts.length > 1) {
    return { account: null, reason: "ambiguous_accounts" };
  }
  return { account: null, reason: "no_account" };
}

/**
 * @param {{ source: string, mode: "connect" | "bootstrap" }} opts
 */
async function runConnectPipeline({ source, mode }) {
  hydrateMedMicAuthFromLatch();

  if (mode === "bootstrap" && getMedMicAuthSnapshot().disconnected) {
    logMedmicAuthDiag("medmic", "bootstrap_blocked_disconnected_latch", {
      source,
    });
    MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
    return false;
  }

  if (connectInFlight) {
    logMedmicAuthDiag("medmic", "connect_join_inflight", { source, mode });
    return connectInFlight;
  }

  connectInFlight = (async () => {
    const snap = getMedMicAuthSnapshot();
    if (
      snap.state === "authenticated" &&
      snap.accessToken &&
      !snap.disconnected
    ) {
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
      return true;
    }

    // Explicit connect clears the latch before acquiring.
    if (mode === "connect" && snap.disconnected) {
      patchMedMicAuthStore({ disconnected: false, reason: null });
    }

    const op = beginOperation(source, {
      rotateSession: mode === "connect" || snap.disconnected,
    });
    const startedAt = Date.now();
    const signal = activeAbortController?.signal;

    try {
      await initializeCreMsal();
      const instance = getCreMsalInstance();
      const { account, reason } = resolveMedmicAccount(mode, instance);
      if (!account) {
        commitFailure(
          op,
          {
            code: reason || "no_account",
            message:
              reason === "ambiguous_accounts"
                ? "Multiple Microsoft accounts are cached. Connect MedMic explicitly."
                : reason === "account_mismatch"
                  ? "Cached account does not match the last MedMic identity."
                  : reason === "no_known_medmic_account"
                    ? "No previous MedMic account — Connect MedMic to continue."
                    : "No CRE MSAL account.",
          },
          mode === "bootstrap" ? "signed_out" : "validation_failed",
        );
        return false;
      }

      let accessToken;
      let acquireTimeoutId;
      try {
        accessToken = await Promise.race([
          getCreAccessToken(instance, account),
          new Promise((_, reject) => {
            acquireTimeoutId = setTimeout(
              () => reject(new Error("acquire_timeout")),
              ACQUIRE_TIMEOUT_MS,
            );
          }),
        ]);
      } catch (err) {
        commitFailure(op, {
          code: "acquire_failed",
          message: err instanceof Error ? err.message : String(err),
        });
        return false;
      } finally {
        if (acquireTimeoutId) clearTimeout(acquireTimeoutId);
      }

      if (!isOperationCurrent(op)) {
        logMedmicAuthDiag("medmic", "acquire_discarded_stale", {
          source,
          operationId: op.operationId,
        });
        return false;
      }

      if (!accessToken) {
        commitFailure(op, {
          code: "no_token",
          message: "MSAL did not return an access token.",
        });
        return false;
      }

      const meta = getSafeTokenMeta(accessToken);
      logMedmicAuthDiag("medmic", "acquire_token_candidate", {
        source,
        operationId: op.operationId,
        fingerprint: meta.fingerprint,
        aud: meta.aud,
        exp: meta.exp,
      });

      if (!isOperationCurrent(op)) return false;

      patchMedMicAuthStore({ state: "validating", operationId: op.operationId });
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });

      const result = await validateMedmicToken(accessToken, { source, signal });

      if (!isOperationCurrent(op)) {
        logMedmicAuthDiag("medmic", "validate_discarded_stale", {
          source,
          operationId: op.operationId,
          validateOk: result.ok,
        });
        return false;
      }

      logMedmicAuthDiag("medmic", "validate_result", {
        source,
        operationId: op.operationId,
        ok: result.ok,
        fingerprint: meta.fingerprint,
        elapsedMs: Date.now() - startedAt,
      });

      if (!result.ok) {
        if (result.error?.code === "validate_aborted") {
          return false;
        }
        commitFailure(op, result.error);
        return false;
      }

      const userEmail =
        result.userEmail ||
        (typeof account.username === "string" ? account.username : "") ||
        null;
      const expiresAt = meta.expiresAtMs ?? Date.now() + DEFAULT_TOKEN_TTL_MS;

      const committed = commitAuthenticated(op, {
        accessToken,
        userEmail,
        user: result.user,
        expiresAt,
        homeAccountId: account.homeAccountId ?? null,
        localAccountId: account.localAccountId ?? null,
      });
      if (!committed) return false;

      MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
      return true;
    } catch (err) {
      commitFailure(op, {
        code: "connect_error",
        message: err instanceof Error ? err.message : String(err),
      });
      return false;
    } finally {
      connectInFlight = null;
    }
  })();

  return connectInFlight;
}

function buildSessionPayload(snap) {
  const publishToken = sessionAllowsToken(snap);
  /** @type {Record<string, unknown>} */
  const payload = {
    type: CRE_DESKTOP_AGENT_SESSION,
    state: snap.state,
    epoch: snap.epoch,
    sessionId: snap.sessionId,
    generation: snap.generation,
    disconnected: Boolean(snap.disconnected) || snap.state === "disconnected",
    userEmail: snap.userEmail ?? null,
    expiresAt: publishToken ? snap.expiresAt ?? null : null,
    operationId: snap.operationId,
    reason: snap.reason ?? undefined,
    errorCode: snap.errorCode ?? undefined,
    refreshSource: snap.refreshSource ?? undefined,
    refreshWarning: Boolean(snap.refreshWarning),
  };
  if (publishToken) {
    payload.accessToken = snap.accessToken;
  }
  return payload;
}

function broadcastSnapshot(payload) {
  if (!broadcastChannel) return;
  try {
    broadcastChannel.postMessage({
      type: "MEDMIC_SNAPSHOT",
      tabId,
      payload,
    });
  } catch {
    /* ignore */
  }
}

function applyRemoteSnapshot(remote) {
  if (!remote || typeof remote !== "object") return false;
  const local = getMedMicAuthSnapshot();
  const cmp = compareMedMicSessionOrder(remote, local);
  if (cmp < 0) {
    logMedmicAuthDiag("medmic", "remote_snapshot_stale_ignored", {
      remoteEpoch: remote.epoch,
      remoteGeneration: remote.generation,
      localEpoch: local.epoch,
      localGeneration: local.generation,
    });
    return false;
  }
  if (cmp === 0 && remote.sessionId === local.sessionId) {
    // Idempotent: still allow state field refresh without token regression.
  }

  const authenticated =
    remote.state === "authenticated" && Boolean(remote.accessToken);

  replaceMedMicAuthStore({
    state: remote.state,
    epoch: remote.epoch,
    sessionId: remote.sessionId,
    generation: remote.generation,
    disconnected:
      Boolean(remote.disconnected) || remote.state === "disconnected",
    accessToken: authenticated ? remote.accessToken : undefined,
    userEmail: remote.userEmail ?? null,
    expiresAt: authenticated ? remote.expiresAt ?? null : null,
    operationId: remote.operationId,
    error: null,
    user: null,
    homeAccountId: local.homeAccountId,
    localAccountId: local.localAccountId,
    reason: remote.reason ?? null,
  });

  // Preserve identity from latch when remote clears token.
  const latch = readMedMicDisconnectLatch();
  if (!getMedMicAuthSnapshot().homeAccountId && latch?.lastAccountIdentifier) {
    patchMedMicAuthStore({
      homeAccountId: latch.lastAccountIdentifier,
      localAccountId: latch.lastLocalAccountId ?? null,
    });
  }

  persistLatchFromSnapshot(getMedMicAuthSnapshot());
  // Publish to extension in this tab without rebroadcasting.
  MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
  const applied = getMedMicAuthSnapshot();
  if (applied.state === "authenticated" && applied.accessToken) {
    scheduleProactiveRefreshTimer();
  } else if (
    applied.disconnected ||
    applied.state === "disconnected" ||
    applied.state === "interaction_required" ||
    applied.state === "expired"
  ) {
    clearProactiveRefreshTimer();
  }
  return true;
}

function ensureBroadcastChannel() {
  if (typeof BroadcastChannel === "undefined") return;
  if (broadcastChannel) return;
  try {
    broadcastChannel = new BroadcastChannel(MEDMIC_AUTH_BROADCAST_CHANNEL);
    broadcastChannel.onmessage = (event) => {
      const data = event.data;
      if (!data || data.type !== "MEDMIC_SNAPSHOT") return;
      if (data.tabId === tabId) return;
      applyRemoteSnapshot(data.payload);
    };
  } catch {
    broadcastChannel = null;
  }
}

function handleAuthRequest(event) {
  if (event.source !== window) return;
  if (event.origin !== window.location.origin) return;
  if (event.data?.type !== CRE_DESKTOP_AGENT_AUTH_REQUEST) return;
  if (!isMedmicExtensionSurface()) return;

  void (async () => {
    await MedMicAuthCoordinator.initialize();
    hydrateMedMicAuthFromLatch();
    const snap = getMedMicAuthSnapshot();

    if (snap.disconnected || snap.state === "disconnected") {
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      return;
    }

    if (shouldRefreshMedMicSession(snap)) {
      logMedmicAuthDiag("medmic", "auth_request_refresh", {
        state: snap.state,
        expired: isTokenExpired(snap.expiresAt),
        hasToken: Boolean(snap.accessToken),
        epoch: snap.epoch,
        generation: snap.generation,
      });
      await MedMicAuthCoordinator.refresh({ source: "auth_request" });
      return;
    }

    MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
  })();
}

function handleDisconnectRequest(event) {
  if (event.source !== window) return;
  if (event.origin !== window.location.origin) return;
  if (event.data?.type !== CRE_EXTENSION_DISCONNECT_REQUEST) return;
  if (!isMedmicExtensionSurface()) return;

  hydrateMedMicAuthFromLatch();
  const snap = getMedMicAuthSnapshot();
  const reason =
    typeof event.data?.reason === "string" ? event.data.reason : "extension";
  const reported = {
    sessionId:
      typeof event.data?.sessionId === "string" ? event.data.sessionId : null,
    epoch: typeof event.data?.epoch === "number" ? event.data.epoch : null,
    generation:
      typeof event.data?.generation === "number"
        ? event.data.generation
        : null,
  };
  const decision = evaluateExtensionDisconnectCommand(reported, snap);
  const meta = {
    reason,
    source: event.data?.source,
    decision: decision.reason,
    reportedSessionId: reported.sessionId,
    reportedEpoch: reported.epoch,
    reportedGeneration: reported.generation,
    localSessionId: snap.sessionId,
    localEpoch: snap.epoch,
    localGeneration: snap.generation,
  };

  if (!decision.accept) {
    logMedmicAuthDiag("medmic", "disconnect_request_rejected", meta);
    return;
  }

  logMedmicAuthDiag("medmic", "disconnect_request_accepted", meta);
  if (decision.reason === "already_disconnected") return;

  MedMicAuthCoordinator.disconnect(reason);
}

/**
 * Decide whether an extension disconnect *command* should run.
 * Unlike SESSION snapshots, a slightly behind generation is still valid when it
 * targets the current session/epoch — it is a user command, not a stale snapshot.
 *
 * @param {{
 *   sessionId?: string | null,
 *   epoch?: number | null,
 *   generation?: number | null,
 * }} reported
 * @param {import("./medmicAuthStore").MedMicAuthSnapshot} snap
 * @returns {{ accept: boolean, reason: string }}
 */
export function evaluateExtensionDisconnectCommand(reported, snap) {
  if (snap.disconnected && snap.state === "disconnected") {
    return { accept: true, reason: "already_disconnected" };
  }

  const hasSession =
    typeof reported.sessionId === "string" && reported.sessionId.length > 0;
  const hasEpoch = typeof reported.epoch === "number";

  // Legacy / unscoped command — accept (bridge may omit targets).
  if (!hasSession && !hasEpoch) {
    return { accept: true, reason: "unscoped_command" };
  }

  // Current session id match — generation may lag; still a valid user command.
  if (hasSession && reported.sessionId === snap.sessionId) {
    return { accept: true, reason: "session_match" };
  }

  // Same epoch as current MedMic session — accept even if generation is behind.
  if (hasEpoch && reported.epoch === snap.epoch) {
    return { accept: true, reason: "epoch_match" };
  }

  // Old session must not tear down a newer MedMic session.
  if (hasEpoch && reported.epoch < snap.epoch) {
    return { accept: false, reason: "stale_epoch" };
  }

  if (hasSession && reported.sessionId !== snap.sessionId) {
    return { accept: false, reason: "session_mismatch" };
  }

  return { accept: false, reason: "untrusted_target" };
}

function handleAuthFailure(event) {
  if (event.source !== window) return;
  if (event.origin !== window.location.origin) return;
  if (event.data?.type !== CRE_EXTENSION_AUTH_FAILURE) return;
  if (!isMedmicExtensionSurface()) return;

  void MedMicAuthCoordinator.handleAuthFailure({
    status: event.data.status,
    sessionId: event.data.sessionId,
    epoch: event.data.epoch,
    generation: event.data.generation,
    operationId: event.data.operationId,
    requestId: event.data.requestId,
    endpoint: event.data.endpoint,
    reason: event.data.reason,
  });
}

export const MedMicAuthCoordinator = {
  getSnapshot: getMedMicAuthSnapshot,

  subscribe(listener) {
    return useMedMicAuthStore.subscribe(listener);
  },

  /**
   * Explicit Connect — only path that clears the disconnect latch.
   * @param {{ source?: string }} [opts]
   */
  async connect(opts = {}) {
    if (!isMedmicExtensionSurface()) {
      logMedmicAuthDiag("medmic", "connect_blocked_not_extension_surface", {
        source: opts.source,
      });
      return false;
    }
    await MedMicAuthCoordinator.initialize();
    return runConnectPipeline({
      source: opts.source ?? "connect",
      mode: "connect",
    });
  },

  /**
   * Controlled silent bootstrap (route init only). Honors disconnect latch
   * and last validated MedMic account.
   * @param {{ source?: string }} [opts]
   */
  async bootstrap(opts = {}) {
    if (!isMedmicExtensionSurface()) return false;
    await MedMicAuthCoordinator.initialize();
    if (getMedMicAuthSnapshot().disconnected) {
      logMedmicAuthDiag("medmic", "bootstrap_blocked_disconnected_latch", {
        source: opts.source,
      });
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      return false;
    }
    return runConnectPipeline({
      source: opts.source ?? "bootstrap",
      mode: "bootstrap",
    });
  },

  /**
   * @deprecated Prefer connect/bootstrap explicitly. Does not clear latch.
   */
  async ensureAuthenticated(opts = {}) {
    hydrateMedMicAuthFromLatch();
    const snap = getMedMicAuthSnapshot();
    if (snap.disconnected) {
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      return false;
    }
    if (snap.state === "authenticated" && snap.accessToken) {
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      return true;
    }
    return MedMicAuthCoordinator.bootstrap({
      source: opts.source ?? "ensure",
    });
  },

  /**
   * Disconnect MedMic only — no MSAL logout, no Enterprise clear.
   * Creates a new authoritative disconnect epoch/session/generation.
   * @param {string} [reason]
   */
  disconnect(reason = "user") {
    hydrateMedMicAuthFromLatch();
    const snap = getMedMicAuthSnapshot();
    const clearIdentity = reason === "switch_account";

    // Idempotent: no epoch rotation, no SESSION republish — except switch_account
    // must still clear sticky identity so the next login is not rebound to the old user.
    if (snap.disconnected && snap.state === "disconnected") {
      if (
        clearIdentity &&
        (snap.userEmail || snap.homeAccountId || snap.localAccountId)
      ) {
        replaceMedMicAuthStore({
          ...snap,
          userEmail: null,
          homeAccountId: null,
          localAccountId: null,
          user: null,
          reason,
        });
        persistLatchFromSnapshot(getMedMicAuthSnapshot());
        MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
      }
      logMedmicAuthDiag("medmic", "disconnect_commit", {
        reason,
        idempotent: true,
        clearIdentity,
        epoch: getMedMicAuthSnapshot().epoch,
        sessionId: getMedMicAuthSnapshot().sessionId,
        generation: getMedMicAuthSnapshot().generation,
      });
      return true;
    }

    clearProactiveRefreshTimer();
    invalidateAllRefreshLeases();
    refreshInFlight = null;

    if (activeAbortController) {
      try {
        activeAbortController.abort();
      } catch {
        /* ignore */
      }
      activeAbortController = null;
    }
    connectInFlight = null;

    const nextEpoch = snap.epoch + 1;
    const nextSessionId = createMedMicSessionId();

    replaceMedMicAuthStore({
      state: "disconnected",
      epoch: nextEpoch,
      sessionId: nextSessionId,
      generation: 0,
      disconnected: true,
      accessToken: undefined,
      userEmail: clearIdentity ? null : snap.userEmail ?? null,
      expiresAt: null,
      operationId: undefined,
      error: null,
      errorCode: null,
      user: null,
      homeAccountId: clearIdentity ? null : snap.homeAccountId ?? null,
      localAccountId: clearIdentity ? null : snap.localAccountId ?? null,
      reason,
      refreshSource: null,
      refreshWarning: false,
    });

    persistLatchFromSnapshot(getMedMicAuthSnapshot());
    clearExtensionHandoffStorage();

    logMedmicAuthDiag("medmic", "disconnect_commit", {
      reason,
      clearIdentity,
      epoch: nextEpoch,
      sessionId: nextSessionId,
      generation: 0,
      priorEpoch: snap.epoch,
      priorSessionId: snap.sessionId,
      priorGeneration: snap.generation,
    });

    MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
    logMedmicAuthDiag("medmic", "disconnect_session_published", {
      reason,
      epoch: nextEpoch,
      sessionId: nextSessionId,
      generation: 0,
    });
    return true;
  },

  /**
   * Proactive, 401-driven, or extension AUTH_REQUEST refresh. Single-flight; Validate before publish.
   * @param {{ source?: "proactive" | "http_401" | "auth_request" }} [opts]
   */
  async refresh(opts = {}) {
    if (!isMedmicExtensionSurface()) return false;
    return runRefreshPipeline({ source: opts.source ?? "proactive" });
  },

  /**
   * Handle CRE_EXTENSION_AUTH_FAILURE from the extension bridge.
   * @param {{
   *   status?: number,
   *   sessionId?: string,
   *   epoch?: number,
   *   generation?: number,
   *   operationId?: string,
   *   requestId?: string,
   *   endpoint?: string,
   *   reason?: string,
   * }} payload
   */
  async handleAuthFailure(payload = {}) {
    hydrateMedMicAuthFromLatch();
    const snap = getMedMicAuthSnapshot();

    logMedmicAuthDiag("medmic", "auth_failure_received", {
      status: payload.status,
      reportedSessionId: payload.sessionId,
      reportedEpoch: payload.epoch,
      reportedGeneration: payload.generation,
      requestId: payload.requestId,
      endpoint: payload.endpoint,
      localEpoch: snap.epoch,
      localGeneration: snap.generation,
      disconnected: snap.disconnected,
    });

    if (snap.disconnected || snap.state === "disconnected") {
      return false;
    }

    if (payload.sessionId && payload.sessionId !== snap.sessionId) {
      logMedmicAuthDiag("medmic", "auth_failure_session_mismatch", {});
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      return false;
    }

    if (typeof payload.epoch === "number" && payload.epoch < snap.epoch) {
      logMedmicAuthDiag("medmic", "auth_failure_stale_epoch", {});
      return false;
    }

    if (typeof payload.epoch === "number" && payload.epoch > snap.epoch) {
      logMedmicAuthDiag("medmic", "auth_failure_future_epoch", {});
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      return false;
    }

    if (
      typeof payload.generation === "number" &&
      payload.generation < snap.generation
    ) {
      logMedmicAuthDiag("medmic", "auth_failure_stale_generation", {});
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      return false;
    }

    if (
      typeof payload.generation === "number" &&
      payload.generation > snap.generation
    ) {
      logMedmicAuthDiag("medmic", "auth_failure_future_generation", {});
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      return false;
    }

    // Matching epoch+generation → refresh.
    return MedMicAuthCoordinator.refresh({ source: "http_401" });
  },

  /**
   * Publish authoritative SESSION (and legacy AUTH only when token publishable).
   * @param {{ broadcast?: boolean }} [opts]
   */
  publishCurrentSession(opts = {}) {
    const shouldBroadcast = opts.broadcast !== false;
    if (!isMedmicExtensionSurface()) {
      logMedmicAuthDiag("medmic", "publish_blocked_not_extension_surface", {});
      return false;
    }

    const snap = getMedMicAuthSnapshot();
    const sessionPayload = buildSessionPayload(snap);
    const publishToken = sessionAllowsToken(snap);

    logMedmicAuthDiag("medmic", "publish_session_snapshot", {
      state: snap.state,
      epoch: snap.epoch,
      generation: snap.generation,
      sessionId: snap.sessionId,
      hasToken: Boolean(sessionPayload.accessToken),
      refreshSource: snap.refreshSource,
      errorCode: snap.errorCode,
    });

    if (publishToken) {
      storeExtensionHandoff({
        accessToken: snap.accessToken,
        expiresAt: snap.expiresAt,
        userEmail: snap.userEmail,
        generation: snap.generation,
        sessionId: snap.sessionId,
        epoch: snap.epoch,
      });
    } else {
      clearExtensionHandoffStorage();
    }

    if (typeof window !== "undefined") {
      window.postMessage(sessionPayload, window.location.origin);
    }

    if (shouldBroadcast) {
      ensureBroadcastChannel();
      broadcastSnapshot(sessionPayload);
    }

    return true;
  },

  /**
   * Await latch sync (content script) then mark coordinator ready.
   * Must complete before bootstrap/connect decisions.
   */
  async initialize() {
    if (coordinatorReady) return;
    if (initializePromise) return initializePromise;

    initializePromise = (async () => {
      hydrateMedMicAuthFromLatch();

      if (typeof window !== "undefined") {
        const already =
          sessionStorage.getItem("medmic_latch_synced_v1") === "1";
        if (!already) {
          await Promise.race([
            new Promise((resolve) => {
              const onReady = () => {
                window.removeEventListener("medmic-latch-ready", onReady);
                resolve(undefined);
              };
              window.addEventListener("medmic-latch-ready", onReady);
            }),
            new Promise((resolve) => setTimeout(resolve, 800)),
          ]);
          // Re-hydrate after content script may have mirrored extension latch.
          hydratedFromLatch = false;
          hydrateMedMicAuthFromLatch();
        }
      }

      coordinatorReady = true;
      logMedmicAuthDiag("medmic", "coordinator_ready", {
        disconnected: getMedMicAuthSnapshot().disconnected,
        state: getMedMicAuthSnapshot().state,
        ...MedMicAuthCoordinator.getEpoch(),
      });
    })();

    return initializePromise;
  },

  isReady() {
    return coordinatorReady;
  },

  startBridge() {
    if (typeof window === "undefined") return;
    if (!isMedmicExtensionSurface()) {
      logMedmicAuthDiag("medmic", "bridge_start_skipped_not_extension_surface", {
        path: window.location.pathname,
      });
      return;
    }
    void MedMicAuthCoordinator.initialize();
    ensureBroadcastChannel();
    if (bridgeStarted) return;
    bridgeStarted = true;
    window.addEventListener("message", (event) => {
      handleAuthRequest(event);
      handleDisconnectRequest(event);
      handleAuthFailure(event);
    });
    const snap = getMedMicAuthSnapshot();
    if (snap.state === "authenticated" && snap.accessToken) {
      scheduleProactiveRefreshTimer();
    }
    logMedmicAuthDiag("medmic", "bridge_started", {
      path: window.location.pathname,
      ...MedMicAuthCoordinator.getEpoch(),
      tabId,
    });
  },

  /** Tear down timers/channels (tests / page unload). */
  dispose() {
    clearProactiveRefreshTimer();
    invalidateAllRefreshLeases();
    refreshInFlight = null;
    connectInFlight = null;
    if (activeAbortController) {
      try {
        activeAbortController.abort();
      } catch {
        /* ignore */
      }
      activeAbortController = null;
    }
    if (broadcastChannel) {
      try {
        broadcastChannel.close();
      } catch {
        /* ignore */
      }
      broadcastChannel = null;
    }
    bridgeStarted = false;
    coordinatorReady = false;
    initializePromise = null;
  },

  /** @deprecated Use disconnect() */
  signOutLocal() {
    return MedMicAuthCoordinator.disconnect("sign_out_local");
  },

  clearHandoffStorage() {
    clearExtensionHandoffStorage();
  },

  getEpoch() {
    const snap = getMedMicAuthSnapshot();
    return {
      sessionId: snap.sessionId,
      generation: snap.generation,
      epoch: snap.epoch,
    };
  },

  isDisconnected() {
    hydrateMedMicAuthFromLatch();
    return getMedMicAuthSnapshot().disconnected;
  },

  _resetForTests() {
    MedMicAuthCoordinator.dispose();
    hydratedFromLatch = false;
    clearRefreshLeaseForTests();
    clearMedMicDisconnectLatchForTests();
    resetMedMicAuthStoreForTests();
  },
};
